<div class="table-wrapper">
  <div *ngIf="showTopSummary" class="table-top-summary">{{ topSummaryText }}</div>

  <div class="table-container">
    <h2 *ngIf="heading" class="table-heading" [id]="headingElementId">{{ heading }}</h2>

    <div *ngIf="selectionModeActive" class="selection-toolbar">
      <div class="selection-toolbar__summary" aria-live="polite">
        <button
          type="button"
          class="selection-toolbar__clear"
          [attr.aria-label]="'Clear ' + selectionNoun + ' selection'"
          [disabled]="selectionDisabled"
          (click)="onClearSelection()"
        >
          <mat-icon fontSet="material-symbols-outlined" aria-hidden="true">close</mat-icon>
        </button>
        <span>{{ selectionSummary }}</span>
      </div>

      <button
        *ngIf="selectionActionLabel"
        type="button"
        class="selection-toolbar__action"
        [disabled]="selectionDisabled"
        (click)="onSelectionAction()"
      >
        {{ selectionActionLabel }}
      </button>
    </div>

    <div *ngIf="selectableRows" class="mobile-selection-control">
      <app-ods-checkbox
        [checkboxId]="selectionIdPrefix + '-mobile-all'"
        [checked]="allVisibleRowsSelected"
        [disabled]="selectionDisabled || selectableVisibleRows.length === 0"
        [ariaLabel]="'Select all ' + selectionNoun + 's displayed on this page'"
        (checkedChange)="onVisibleSelectionToggle($event)"
      >
        Select all {{ selectionNoun }}s
      </app-ods-checkbox>
    </div>

    <table
      mat-table
      [dataSource]="dataSource ?? []"
      class="results-table full-width-table"
      [class.results-table--static]="!rowHover"
      [attr.aria-label]="ariaLabel || null"
      [attr.aria-labelledby]="heading && !ariaLabel ? headingElementId : null"
      matSort
      [matSortActive]="sortActive"
      [matSortDirection]="sortDirection"
      matSortDisableClear
      (matSortChange)="onSortChanged($event)"
    >
      <ng-container *ngIf="selectableRows" matColumnDef="tableSelection">
        <th mat-header-cell *matHeaderCellDef scope="col" class="selection-column">
          <app-ods-checkbox
            class="selection-checkbox"
            [class.is-hidden]="!shouldShowHeaderSelection()"
            [checkboxId]="selectionIdPrefix + '-all'"
            [checked]="allVisibleRowsSelected"
            [disabled]="selectionDisabled || selectableVisibleRows.length === 0"
            [compact]="true"
            [preview]="headerHovered && !allVisibleRowsSelected"
            [ariaLabel]="'Select all ' + selectionNoun + 's displayed on this page'"
            (click)="$event.stopPropagation()"
            (checkedChange)="onVisibleSelectionToggle($event)"
          ></app-ods-checkbox>
        </th>

        <td
          mat-cell
          *matCellDef="let row"
          class="selection-column"
          [attr.data-label]="'Select ' + selectionNoun"
          [style.height.px]="rowHeight"
        >
          <app-ods-checkbox
            *ngIf="rowSelectable(row)"
            class="selection-checkbox"
            [class.is-hidden]="!shouldShowRowSelection(row)"
            [checkboxId]="selectionCheckboxId(row)"
            [checked]="isRowSelected(row)"
            [disabled]="selectionDisabled"
            [compact]="true"
            [preview]="!selectionModeActive && isRowHovered(row)"
            [ariaLabel]="(isRowSelected(row) ? 'Deselect' : 'Select') + ' ' + selectionNoun + ' ' + rowId(row)"
            (click)="$event.stopPropagation()"
            (checkedChange)="onRowSelectionToggle(row, $event)"
          ></app-ods-checkbox>
        </td>
      </ng-container>

      <ng-container *ngFor="let column of columns" [matColumnDef]="column.id">
        <th
          mat-header-cell
          *matHeaderCellDef
          scope="col"
          [attr.aria-sort]="ariaSortFor(column)"
          [class.align-right]="isActionColumnAlignedRight(column)"
          [class.align-center]="column.align === 'center'"
          [style.width.px]="column.columnWidth"
        >
          <ng-container *ngIf="column.sortable; else staticHeader">
            <span [mat-sort-header]="column.sortId || column.id">{{ column.showHeader ? column.header : '' }}</span>
          </ng-container>
          <ng-template #staticHeader>{{ column.showHeader ? column.header : '' }}</ng-template>
        </th>

        <td
          mat-cell
          *matCellDef="let row"
          [class.action-column]="isActionColumnAlignedRight(column)"
          [class.align-center]="column.align === 'center'"
          [attr.data-label]="
            column.showHeader ? column.header : column.type === 'actionGroup' ? 'Actions' : column.header
          "
          [style.width.px]="column.columnWidth"
          [style.height.px]="rowHeight"
        >
          <ng-container [ngSwitch]="column.type">
            <span *ngSwitchCase="'text'">{{ getText(column, row) }}</span>

            <span *ngSwitchCase="'textList'" class="text-list-cell">
              <span *ngFor="let value of getTextList(column, row); trackBy: trackByValue" class="stacked-value">{{
                value
              }}</span>
            </span>

            <ng-container *ngSwitchCase="'link'">
              <ng-container *ngIf="getLink(column, row) as link">
                <a
                  *ngIf="link.href; else plainLinkLabel"
                  class="table-link"
                  [href]="link.href"
                  [target]="link.target || '_self'"
                  [attr.rel]="linkRel(link)"
                  [attr.aria-label]="linkAriaLabel(link)"
                  (click)="onLinkClick($event, link)"
                >
                  {{ link.label }}
                </a>
                <ng-template #plainLinkLabel>
                  <span>{{ link.label }}</span>
                </ng-template>
              </ng-container>
            </ng-container>

            <span *ngSwitchCase="'links'" class="link-list-cell">
              <ng-container *ngFor="let link of getLinks(column, row); trackBy: trackByLink">
                <a
                  *ngIf="link.href; else plainListLabel"
                  class="stacked-value table-link"
                  [href]="link.href"
                  [target]="link.target || '_self'"
                  [attr.rel]="linkRel(link)"
                  [attr.aria-label]="linkAriaLabel(link)"
                  (click)="onLinkClick($event, link)"
                >
                  {{ link.label }}
                </a>
                <ng-template #plainListLabel>
                  <span class="stacked-value">{{ link.label }}</span>
                </ng-template>
              </ng-container>
            </span>

            <span *ngSwitchCase="'icons'" class="icon-list">
              <mat-icon
                *ngFor="let icon of getIcons(column, row); trackBy: trackByIcon"
                class="mr-1"
                fontSet="material-symbols-outlined"
                role="img"
                [matTooltip]="icon.tooltip"
                aria-hidden="false"
                [attr.aria-label]="icon.ariaLabel || icon.tooltip"
              >
                {{ icon.iconName }}
              </mat-icon>
            </span>

            <span *ngSwitchCase="'iconText'" class="icon-text-cell">
              <ng-container *ngIf="getIcon(column, row) as icon">
                <mat-icon
                  fontSet="material-symbols-outlined"
                  role="img"
                  [matTooltip]="icon.tooltip"
                  aria-hidden="false"
                  [attr.aria-label]="icon.ariaLabel || icon.tooltip"
                >
                  {{ icon.iconName }}
                </mat-icon>
              </ng-container>
              <span>{{ getText(column, row) }}</span>
            </span>

            <ng-container *ngSwitchCase="'status'">
              <app-status-badge
                *ngIf="getStatus(column, row) as status"
                [label]="status.label"
                [tone]="status.tone"
              ></app-status-badge>
            </ng-container>

            <ng-container *ngSwitchCase="'statusIcon'">
              <app-status-badge
                *ngIf="getStatus(column, row) as status"
                [label]="status.label"
                [tone]="status.tone"
                [icon]="status.icon ?? null"
                [iconOnly]="true"
                [matTooltip]="status.label"
              ></app-status-badge>
            </ng-container>

            <span *ngSwitchCase="'favourite'" class="favourite-cell">
              <button
                type="button"
                mat-icon-button
                class="favourite-button"
                [class.is-hidden]="!shouldShowFavourite(column, row)"
                [disabled]="isFavouriteDisabled(column, row)"
                [matTooltip]="favouriteLabel(column, row)"
                [attr.aria-label]="favouriteLabel(column, row)"
                (click)="onFavouriteToggle(row, $event)"
              >
                <svg
                  class="ontario-icon favourite-icon"
                  focusable="false"
                  aria-hidden="true"
                  [class.is-favourite]="isFavourite(column, row)"
                >
                  <use
                    [attr.href]="
                      isFavourite(column, row)
                        ? 'assets/ontario-design-system/icons/ontario-icons-secondary.svg#ontario-icon-favourite-on'
                        : 'assets/ontario-design-system/icons/ontario-icons-secondary.svg#ontario-icon-favourite-off'
                    "
                  ></use>
                </svg>
              </button>
            </span>

            <span *ngSwitchCase="'actionGroup'" class="action-cell-content">
              <ng-container *ngIf="getActions(column, row) as actions">
                <a
                  *ngIf="actions.link?.href"
                  class="action-link"
                  [class.is-hidden]="!isRowHovered(row)"
                  [href]="actions.link.href"
                  [target]="actions.link.target || '_self'"
                  [attr.rel]="linkRel(actions.link)"
                  [attr.aria-label]="linkAriaLabel(actions.link)"
                  (click)="onLinkClick($event, actions.link)"
                >
                  {{ actions.link.label }}
                </a>

                <button
                  *ngIf="actions.button && actions.button.variant === 'raised'"
                  mat-raised-button
                  [color]="actions.button.color || 'primary'"
                  [ngClass]="actions.button.cssClass"
                  [disabled]="!!actions.button.disabled"
                  [attr.aria-label]="actions.button.ariaLabel || actions.button.label"
                  (click)="onActionButtonClick(row, column, $event, actions.button)"
                >
                  {{ actions.button.label }}
                </button>

                <button
                  *ngIf="actions.button && actions.button.variant === 'stroked'"
                  mat-stroked-button
                  [color]="actions.button.color || 'primary'"
                  [ngClass]="actions.button.cssClass"
                  [disabled]="!!actions.button.disabled"
                  [attr.aria-label]="actions.button.ariaLabel || actions.button.label"
                  (click)="onActionButtonClick(row, column, $event, actions.button)"
                >
                  {{ actions.button.label }}
                </button>

                <button
                  *ngIf="actions.secondaryButton && actions.secondaryButton.variant === 'raised'"
                  mat-raised-button
                  [color]="actions.secondaryButton.color || 'primary'"
                  [ngClass]="actions.secondaryButton.cssClass"
                  [disabled]="!!actions.secondaryButton.disabled"
                  [attr.aria-label]="actions.secondaryButton.ariaLabel || actions.secondaryButton.label"
                  (click)="onActionButtonClick(row, column, $event, actions.secondaryButton)"
                >
                  {{ actions.secondaryButton.label }}
                </button>

                <button
                  *ngIf="actions.secondaryButton && actions.secondaryButton.variant === 'stroked'"
                  mat-stroked-button
                  [color]="actions.secondaryButton.color || 'primary'"
                  [ngClass]="actions.secondaryButton.cssClass"
                  [disabled]="!!actions.secondaryButton.disabled"
                  [attr.aria-label]="actions.secondaryButton.ariaLabel || actions.secondaryButton.label"
                  (click)="onActionButtonClick(row, column, $event, actions.secondaryButton)"
                >
                  {{ actions.secondaryButton.label }}
                </button>

                <button
                  *ngIf="actions.tertiaryButton && actions.tertiaryButton.variant === 'raised'"
                  mat-raised-button
                  [color]="actions.tertiaryButton.color || 'primary'"
                  [ngClass]="actions.tertiaryButton.cssClass"
                  [disabled]="!!actions.tertiaryButton.disabled"
                  [attr.aria-label]="actions.tertiaryButton.ariaLabel || actions.tertiaryButton.label"
                  (click)="onActionButtonClick(row, column, $event, actions.tertiaryButton)"
                >
                  {{ actions.tertiaryButton.label }}
                </button>

                <button
                  *ngIf="actions.tertiaryButton && actions.tertiaryButton.variant === 'stroked'"
                  mat-stroked-button
                  [color]="actions.tertiaryButton.color || 'primary'"
                  [ngClass]="actions.tertiaryButton.cssClass"
                  [disabled]="!!actions.tertiaryButton.disabled"
                  [attr.aria-label]="actions.tertiaryButton.ariaLabel || actions.tertiaryButton.label"
                  (click)="onActionButtonClick(row, column, $event, actions.tertiaryButton)"
                >
                  {{ actions.tertiaryButton.label }}
                </button>

                <button
                  *ngIf="actions.menuItems?.length"
                  type="button"
                  mat-icon-button
                  class="table-actions-button"
                  [matMenuTriggerFor]="tableActionsMenu"
                  [matMenuTriggerData]="{ row: row, column: column, actions: actions }"
                  [attr.aria-label]="actions.menuAriaLabel || 'Actions'"
                  (click)="$event.stopPropagation()"
                >
                  <span class="table-actions-icon" aria-hidden="true"></span>
                </button>
              </ng-container>
            </span>
          </ng-container>
        </td>
      </ng-container>

      <tr
        mat-header-row
        *matHeaderRowDef="displayedColumnIds"
        [style.height.px]="headerRowHeight"
        (mouseenter)="onHeaderEnter()"
        (mouseleave)="onHeaderLeave()"
      ></tr>
      <tr
        mat-row
        *matRowDef="let row; columns: displayedColumnIds"
        (click)="onRowClick(row)"
        (mouseenter)="onRowEnter(row)"
        (mouseleave)="onRowLeave()"
        [class.is-selected]="isRowSelected(row)"
      ></tr>
      <ng-container *ngIf="noDataText">
        <tr class="mat-mdc-row table-no-data-row" *matNoDataRow>
          <td class="mat-mdc-cell table-no-data-cell" [attr.colspan]="displayedColumnIds.length">
            {{ noDataText }}
          </td>
        </tr>
      </ng-container>
    </table>
    <ng-content select="[tableFooter]"></ng-content>
  </div>

  <mat-menu #tableActionsMenu="matMenu" class="table-actions-menu">
    <ng-template matMenuContent let-row="row" let-column="column" let-actions="actions">
      <button
        *ngFor="let action of actions.menuItems"
        mat-menu-item
        type="button"
        (click)="onActionButtonClick(row, column, $event, action)"
      >
        {{ action.label }}
      </button>
    </ng-template>
  </mat-menu>

  <div class="table-footer" *ngIf="showPagination">
    <div class="table-footer-left">
      <div *ngIf="showRangeSummary" class="range-summary" aria-live="polite">
        Displaying {{ rangeStart }} - {{ rangeEnd }} of {{ length }} {{ resultNoun }}
      </div>

      <nav class="page-links" *ngIf="totalPages > 1" [attr.aria-label]="paginationLabel">
        <span *ngIf="showLeadingEllipsis" class="page-separator" aria-hidden="true">...</span>

        <ng-container *ngFor="let page of visiblePages; let last = last">
          <button
            type="button"
            class="page-link"
            [class.active]="page === currentPage"
            [attr.aria-label]="'Page ' + page"
            [attr.aria-current]="page === currentPage ? 'page' : null"
            (click)="onPageClick(page)"
          >
            {{ page }}
          </button>
          <span *ngIf="!last" class="page-separator" aria-hidden="true">&nbsp;-&nbsp;</span>
        </ng-container>

        <span *ngIf="showTrailingEllipsis" class="page-separator" aria-hidden="true">...</span>
      </nav>

      <div class="page-links">
        <button
          type="button"
          class="page-back"
          *ngIf="showBackControl"
          [attr.aria-label]="'Go to previous page, page ' + backTargetPage"
          (click)="onPageClick(backTargetPage)"
        >
          &lt;&lt; Back
        </button>

        <button
          type="button"
          class="page-next"
          [class.page-next-margin]="showBackControl"
          *ngIf="showNextControl"
          [attr.aria-label]="'Go to next page, page ' + nextTargetPage"
          (click)="onPageClick(nextTargetPage)"
        >
          Next &gt;&gt;
        </button>
      </div>
    </div>

    <div class="table-footer-right">
      <label class="page-size-label" [attr.for]="pageSizeSelectId">Results per page</label>
      <mat-form-field appearance="outline" class="page-size-field">
        <mat-select
          [id]="pageSizeSelectId"
          [value]="pageSize"
          panelClass="table-page-size-panel"
          [attr.aria-label]="ariaLabel ? ariaLabel + ' results per page' : 'Results per page'"
          (selectionChange)="onPageSizeSelectionChange($event.value)"
        >
          <mat-option *ngFor="let option of pageSizeOptions" [value]="option">{{ option }}</mat-option>
        </mat-select>
      </mat-form-field>
    </div>
  </div>
</div>
